import type { KeyboardEvent } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';

export interface AquariumSwitcherItemAquarium {
  id: string;
  name: string;
  volume: number | null;
  volumeUnit: 'liters' | 'gallons';
}

export interface AquariumSwitcherItemProps {
  id: string;
  aquarium: AquariumSwitcherItemAquarium;
  isSelected: boolean;
  isHighlighted: boolean;
  onSelect: (id: string) => void;
  onMouseEnter?: () => void;
}

export function AquariumSwitcherItem({
  id,
  aquarium,
  isSelected,
  isHighlighted,
  onSelect,
  onMouseEnter,
}: AquariumSwitcherItemProps) {
  const { t } = useTranslation();

  const unitLabel =
    aquarium.volumeUnit === 'gallons'
      ? t('header.aquariumSwitcher.unit.gallons')
      : t('header.aquariumSwitcher.unit.liters');

  const handleClick = (): void => {
    onSelect(aquarium.id);
  };

  // Keyboard navigation normally stays on the listbox (aria-activedescendant);
  // if the option itself holds focus (e.g. after a click), Enter/Space select it.
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onSelect(aquarium.id);
    }
  };

  return (
    <div
      id={id}
      role="option"
      aria-selected={isSelected}
      data-active={isHighlighted ? 'true' : 'false'}
      tabIndex={-1}
      className={`aquarium-switcher__item${isHighlighted ? ' aquarium-switcher__item--active' : ''}${
        isSelected ? ' aquarium-switcher__item--selected' : ''
      }`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      title={t('header.aquariumSwitcher.switchTo', { name: aquarium.name })}
    >
      <span className="aquarium-switcher__item-name">{aquarium.name}</span>
      {aquarium.volume !== null && (
        <span className="aquarium-switcher__item-volume">
          {aquarium.volume} {unitLabel}
        </span>
      )}
    </div>
  );
}
